import { h } from "preact";
import { useEffect, useState } from "preact/hooks";
import { RoutableProps } from "preact-router";
import { ScanPackQR } from "../../../components/ScanPackQR";
import "./styles.scss";
import { SerialNumberType } from "../../../data/types";
import { Snackbar, SnackbarShowCommand } from "../../../components/Snackbar";
import { useCommand, useForceUpdate } from "../../../hooks";
import { addProductToDatabase, getSerialNumberInfo, Product } from "../../../api/product-api";

const serialRegexSurfacePro = /^([0-9]{10,20})$/m;

export function LunaEMGTablet(props: RoutableProps) {
    const [tabletType, setTabletType] = useState("");
    const [serialNumber, setSerialNumber] = useState("");
    const [isSerialNumberValid, setIsSerialNumberValid] = useState(false);
    const [serialNumbers, setSerialNumbers] = useState([] as { type: string, serialNumber: string, valid: boolean, registered?: boolean }[]);
    const [typeInfo, setTypeInfo] = useState(null as { type: SerialNumberType, data: Product } | null);
    const forceUpdate = useForceUpdate();
    const showSnackbar = useCommand<SnackbarShowCommand>();

    useEffect(() => {
        setIsSerialNumberValid(false);

        if (!serialNumber) {
            setTypeInfo(null);
            return;
        }

        if (!serialNumber.match(serialRegexSurfacePro)) {
            setTypeInfo(null);
            showSnackbar.run("Podany numer seryjny jest w złym formacie", "error", "error", 3000);
            return;
        }

        setIsSerialNumberValid(true);
    }, [serialNumber]);

    useEffect(() => {
        getSerialNumberInfo(serialNumbers.map(v => v.serialNumber)).then((products) => {
            for (let i = 0; i < serialNumbers.length; i++) {
                serialNumbers[i].valid = true;
                delete serialNumbers[i].registered;
            }

            for (let i = 0; i < products.length; i++) {
                const product = products[i];
                const serialNumberData = serialNumbers.find(v => v.serialNumber === product.serialNumber);

                if (serialNumberData) {
                    serialNumberData.valid = false;
                }
            }

            forceUpdate();
        });
    }, [serialNumbers]);

    return <div class="luna-emg-tablet-page">
        <Snackbar doShow={showSnackbar}/>
        <div class="paper serial-input">
            <ScanPackQR onRead={(data) => {
                const text = JSON.parse(data) as string;
                let match = serialRegexSurfacePro.exec(text);

                if (!match || match.length < 2) {
                    return;
                }

                setSerialNumber(match[1].trim());
            }}/>
            <div>
                <label>
                    <span style="margin-right: 10px;">Wybierz rodzaj tabletu</span>
                    <select value={tabletType} onChange={(e) => setTabletType(e.currentTarget.value)}>
                        <option value="ACC-Tablet-Surface-Pro-7">Surface Pro 7 Tablet</option>
                        <option value="ACC-Tablet-Surface-Pro-6">Surface Pro 6 Tablet</option>
                        <option value="ACC-Tablet-Surface-Pro-2017">Surface Pro (2017) Tablet</option>
                        <option value="ACC-Tablet-Surface-Pro-4">Surface Pro 4 Tablet</option>
                        <option value="ACC-Tablet-Surface-Pro-3">Surface Pro 3 Tablet</option>
                        <option value="ACC-Tablet-Advantech-POC-615">Advanech POC-615 Tablet</option>
                    </select>
                </label>
            </div>
            <div style="margin-top: 10px;">
                <label>
                    <span style="margin-right: 10px;">Podaj lub zeskanuj numer seryjny tabletu</span>
                    <input value={serialNumber} onChange={e => {
                        setSerialNumber(e.currentTarget.value.trim());
                        e.currentTarget.setCustomValidity(!e.currentTarget.value.match(serialRegexSurfacePro) ? "Nieprawidłowy numer seryjny" : "");
                    }} />
                </label>
            </div>
            <div style="margin-top: 10px;">
                <button class={isSerialNumberValid && tabletType ? "" : "disabled"} onClick={() => {
                    if (!tabletType || serialNumbers.find(v => serialNumber === v.serialNumber)) {
                        return;
                    }

                    setSerialNumbers([
                        ...serialNumbers,
                        {
                            type: tabletType,
                            serialNumber,
                            valid: false
                        }
                    ]);
                }}>Dodaj Tablet</button>
            </div>
            <div class="paper-2 serial-list">
                <h2 style="padding-bottom: 10px;">Tablety do rejestracji</h2>
                <table style="width: 100%; text-align: left;">
                    <thead>
                        <th>Typ</th>
                        <th>Numer seryjny</th>
                        <th>Walidacja</th>
                        <th>Usuń</th>
                    </thead>
                    <tbody>
                        {serialNumbers.map((v, i) => <tr class={(v.registered === true ? "register-success" : v.registered === false ? "register-fail" : "")}>
                            <td style="white-space: nowrap">{v.type}</td>
                            <td style="width: 100%">{v.serialNumber}</td>
                            <td class="validation">{!v.valid ? <button class="icon r26 error tooltip-accent tooltip-bottom" data-tooltip="Tablet o podanym numerze seryjnym istnieje już w bazie danych">priority_high</button>
                                : <button class="icon r26 success tooltip-accent tooltip-bottom" data-tooltip="Numer seryjny jest poprawny">check</button>}
                            </td>
                            <td class="remove">
                                <button type="button" class="flat icon error tooltip-top" data-tooltip="Usuwa pozycje z listy" onClick={() => {
                                    serialNumbers.splice(i, 1);
                                    setSerialNumbers([...serialNumbers]);
                                }}>close</button>
                            </td>
                        </tr>)}
                    </tbody>
                </table>
            </div>
            <div style="margin-top: 10px">
                <button type="button" class={serialNumbers.length > 0 && serialNumbers.every(v => v.valid) ? "" : "disabled"} onClick={async () => {
                    const promises = [];

                    for(let i = 0; i < serialNumbers.length; i++) {
                        const serialNumberData = serialNumbers[i];

                        promises.push(addProductToDatabase(serialNumberData.type, serialNumberData.serialNumber, serialNumberData.type).then(() => {
                            serialNumberData.registered = true;
                            forceUpdate();
                        }).catch((ex) => {
                            serialNumberData.registered = false;
                            forceUpdate();
                            throw ex;
                        }));
                    }

                    try {
                        await Promise.all(promises);
                        showSnackbar.run("Wszystkie tablety zostały zarejestrowane pomyślnie", "success", "check");
                    }
                    catch(ex) {
                        console.error(ex);
                        showSnackbar.run("Nie udało się zarejestrować niektórych tabletów", "error", "check");
                    }
                }}>Zarejestruj</button>
            </div>
        </div>
    </div>;
}
